import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchLastPriceForMints } from '../utils/tokens'
import { milliseconds } from 'date-fns'
import { TOKEN_WATCHLIST_KEY } from '@components/watchlist/WatchlistButton'
import { useLocalStorage } from './useLocalStorageState'

export function useWatchlist() {
  const [watchlist] = useLocalStorage(TOKEN_WATCHLIST_KEY)
  const watchlistMints = watchlist.map(({ mint }) => mint)

  const {
    data: watchlistMintPrices,
    isLoading: watchlistPricesLoading,
    refetch: refetchWatchlistPrices,
  } = useQuery({
    queryKey: ['watchlist-token-prices', watchlistMints],
    queryFn: async () =>
      (await fetchLastPriceForMints(watchlistMints)).reduce(
        (acc, { mint, price }) => {
          acc[mint] = price
          return acc
        },
        {} as Record<string, number>,
      ),
    enabled: !!watchlistMints.length,
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    refetchInterval: milliseconds({ seconds: 10 }),
    refetchOnWindowFocus: false,
  })

  const watchlistWithPrices = useMemo(
    () =>
      watchlist.map((token) => ({
        ...token,
        price: watchlistMintPrices?.[token.mint] ?? null,
      })),
    [watchlist, watchlistMintPrices],
  )

  return {
    watchlist: watchlistWithPrices,
    watchlistPricesLoading,
    refetchWatchlistPrices,
  }
}
